import { useMemo, useRef } from 'react';
import { EnhancedAutocomplete, FullRow, IconButton, Typography, Row, FullColumn } from 'seagull';
import { CloseLiner24, EditLine, EyeLine, TrashLine } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useDeleteLegalTerms } from "./hooks/useDeleteLegalTerms";
import { useLegalTerms } from "./hooks/useLegalTerms";
import { useRenameLegalTerms } from "./hooks/useRenameLegalTerms";
import { useUploadLegalTermsWithCustomName } from "./hooks/useUploadLegalTermsWithCustomName/useUploadLegalTermsWithCustomName";
import { useViewLegalTerms } from "./hooks/useViewLegalTerms";
import styles from "./LegalTermsDropdown.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const GetLegalTermsDropdownTID = testid => getTestIds(testid);
export const LegalTermsDropdown = _ref => {
  let {
    selectedLegalTermId,
    onSelectLegalTerm,
    testid,
    disabled,
    entityLegalTerms,
    onRemoveLegalTerm,
    viewMode = 'basic'
  } = _ref;
  const isAdvancedViewMode = viewMode === 'advanced';
  const {
    LegalTermsDropdownTID
  } = useMemo(() => GetLegalTermsDropdownTID(testid), [testid]);
  const {
    legalTerms,
    isLoading: isLoadingLegalTerms
  } = useLegalTerms();
  const {
    viewLegalTerms
  } = useViewLegalTerms();
  const {
    deleteLegalTerms,
    isPending: isDeletingLegalTerms
  } = useDeleteLegalTerms({
    onSuccess: () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    confirmActionTestId: LegalTermsDropdownTID.DeleteLegalTermConfirmModal()
  });
  const {
    renameLegalTerms,
    isPending: isRenamingLegalTerms
  } = useRenameLegalTerms({
    errorToastTestId: LegalTermsDropdownTID.RenameErrorToast()
  });
  const fileInputRef = useRef(null);
  const allLegalTerms = useMemo(() => {
    if (entityLegalTerms) {
      const existingIds = new Set(legalTerms.map(lt => lt.id));
      const uniqueEntityLegalTerms = entityLegalTerms.filter(entityLT => !existingIds.has(entityLT.id));
      return [...legalTerms, ...uniqueEntityLegalTerms];
    }
    return legalTerms;
  }, [entityLegalTerms, legalTerms]);
  const {
    uploadLegalTerms,
    isLoading: isUploadingLegalTerms
  } = useUploadLegalTermsWithCustomName({
    errorToastTestId: LegalTermsDropdownTID.UploadErrorToast(),
    onSettled: newLegalTerms => {
      if (newLegalTerms) {
        onSelectLegalTerm(newLegalTerms);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  });
  const handlePDFUpload = async e => {
    e.preventDefault();
    const file = e.target.files && e.target.files[0];
    uploadLegalTerms(file);
  };
  const isLoading = [isLoadingLegalTerms, isUploadingLegalTerms, isDeletingLegalTerms, isRenamingLegalTerms].some(Boolean);
  const selectedOption = useMemo(() => {
    return selectedLegalTermId ? allLegalTerms.find(legalTerm => legalTerm.id === selectedLegalTermId) : undefined;
  }, [selectedLegalTermId, allLegalTerms]);
  return /*#__PURE__*/_jsxs(FullRow, {
    gap: 16,
    centered: true,
    children: [/*#__PURE__*/_jsx("input", {
      ref: fileInputRef,
      type: "file",
      accept: ".pdf",
      onChange: handlePDFUpload,
      "data-testid": LegalTermsDropdownTID.UploadFileInput(),
      hidden: true
    }), /*#__PURE__*/_jsx(EnhancedAutocomplete, {
      options: allLegalTerms,
      isLoading: isLoading,
      className: styles.LegalTermsDropdown,
      keyField: "id",
      labelField: "name",
      itemRenderer: data => /*#__PURE__*/_jsxs(FullRow, {
        gap: 4,
        spaceBetween: true,
        alignItems: "start",
        children: [/*#__PURE__*/_jsxs(FullColumn, {
          gap: 4,
          alignItems: "start",
          children: [/*#__PURE__*/_jsx(Typography, {
            skin: "secondary",
            variant: "paragraph1",
            children: data.name
          }), /*#__PURE__*/_jsx(Typography, {
            skin: "secondary",
            variant: "paragraph2",
            children: data.contentType === 'pdf' ? "".concat(data.name, ".pdf") : data.name
          })]
        }), isAdvancedViewMode && /*#__PURE__*/_jsxs(Row, {
          children: [/*#__PURE__*/_jsx(IconButton, {
            size: "medium",
            onClick: e => {
              e.stopPropagation();
              renameLegalTerms(data);
            },
            "data-testid": LegalTermsDropdownTID.Dropdown.Item.EditButton(data.id),
            children: /*#__PURE__*/_jsx(EditLine, {})
          }), /*#__PURE__*/_jsx(IconButton, {
            size: "medium",
            onClick: e => {
              e.stopPropagation();
              deleteLegalTerms(data);
            },
            "data-testid": LegalTermsDropdownTID.Dropdown.Item.DeleteButton(data.id),
            children: /*#__PURE__*/_jsx(TrashLine, {})
          })]
        })]
      }),
      size: "medium",
      textFieldProps: {
        placeholder: 'Choose a document',
        label: 'Terms of Services'
      },
      selectedOption: selectedOption,
      applyOptionChange: option => {
        onSelectLegalTerm(option);
      },
      onAddItemClick: async () => {
        var _fileInputRef$current;
        (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 || _fileInputRef$current.click();
      },
      emptySearchText: "No matches found. Try again or create a new legal term.",
      addNewItemText: "Upload PDF",
      "data-testid": LegalTermsDropdownTID.Dropdown.Root(),
      disabled: disabled
    }), onRemoveLegalTerm && /*#__PURE__*/_jsx(IconButton, {
      size: "medium",
      "data-testid": LegalTermsDropdownTID.RemoveButton(),
      onClick: () => onRemoveLegalTerm(selectedOption),
      children: /*#__PURE__*/_jsx(CloseLiner24, {})
    }), /*#__PURE__*/_jsx(IconButton, {
      size: "medium",
      "data-testid": LegalTermsDropdownTID.ViewLegalTermsButton(),
      disabled: !selectedOption,
      onClick: () => {
        if (selectedOption) {
          viewLegalTerms(selectedOption);
        }
      },
      children: /*#__PURE__*/_jsx(EyeLine, {})
    })]
  });
};